import {
  ensureClarity,
  identifyClarityUser,
  setClarityTags,
  trackClarityEvent,
  type ClarityTags,
} from './clarity';

const GA4_MEASUREMENT_ID = 'G-8F1YY2Y9RM';
const GA4_SCRIPT_ID = 'genseo-ga4-gtag';

type AnalyticsTagValue = string | string[] | number | boolean | null | undefined;
export type AnalyticsTags = Record<string, AnalyticsTagValue>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let googleAnalyticsInitialized = false;
let latestGoogleContext: {
  shopDomain?: string;
  sessionId?: string;
  environment?: string;
  metadata?: AnalyticsTags;
} = {};

const toSnakeCase = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .toLowerCase();

const sanitizeAnalyticsValue = (value: AnalyticsTagValue): string | number | boolean | null => {
  if (value === null || typeof value === 'undefined') return null;
  if (Array.isArray(value)) {
    const sanitized = value
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(',');
    return sanitized || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  const sanitized = String(value).trim();
  return sanitized || null;
};

const toGoogleParams = (tags: AnalyticsTags) =>
  Object.entries(tags).reduce<Record<string, string | number | boolean>>((params, [key, value]) => {
    const sanitized = sanitizeAnalyticsValue(value);
    if (sanitized === null) return params;
    params[toSnakeCase(key)] = sanitized;
    return params;
  }, {});

const loadGoogleTag = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer!.push(arguments);
    };

  if (!document.getElementById(GA4_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GA4_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  return true;
};

export const ensureGoogleAnalytics = ({
  shopDomain,
  sessionId,
  environment,
  metadata = {},
}: {
  shopDomain?: string;
  sessionId?: string;
  environment?: string;
  metadata?: AnalyticsTags;
} = {}) => {
  if (!loadGoogleTag() || !window.gtag) return false;

  try {
    latestGoogleContext = {
      shopDomain: shopDomain || latestGoogleContext.shopDomain,
      sessionId: sessionId || latestGoogleContext.sessionId,
      environment: environment || latestGoogleContext.environment,
      metadata: {
        ...(latestGoogleContext.metadata || {}),
        ...metadata,
      },
    };

    if (!googleAnalyticsInitialized) {
      window.gtag('js', new Date());
      window.gtag('consent', 'default', {
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
      googleAnalyticsInitialized = true;
    }

    const userProperties = toGoogleParams({
      appSurface: 'shopify-admin',
      environment: latestGoogleContext.environment,
      shopDomain: latestGoogleContext.shopDomain,
      appSessionId: latestGoogleContext.sessionId,
      ...(latestGoogleContext.metadata || {}),
    });

    window.gtag('set', 'user_properties', userProperties);
    window.gtag('config', GA4_MEASUREMENT_ID, {
      send_page_view: false,
      user_id: latestGoogleContext.shopDomain || undefined,
      user_properties: userProperties,
    });

    return true;
  } catch {
    return false;
  }
};

export const trackGooglePageView = ({
  path,
  title,
  location,
  tags = {},
}: {
  path: string;
  title?: string;
  location?: string;
  tags?: AnalyticsTags;
}) => {
  if (!ensureGoogleAnalytics() || !window.gtag) return;

  try {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: location || (typeof window !== 'undefined' ? window.location.href : path),
      page_title: title || (typeof document !== 'undefined' ? document.title : path),
      ...toGoogleParams(tags),
    });
  } catch {
    // Analytics should never affect the app UX.
  }
};

export const trackAnalyticsEvent = (eventName: string, tags: AnalyticsTags = {}) => {
  trackClarityEvent(eventName, tags as ClarityTags);

  if (!eventName || !ensureGoogleAnalytics() || !window.gtag) return;

  try {
    window.gtag('event', eventName, toGoogleParams(tags));
  } catch {
    // Analytics should never affect the app UX.
  }
};

export const identifyAnalyticsUser = ({
  shopDomain,
  sessionId,
  pageId,
  friendlyName,
  environment,
  metadata = {},
}: {
  shopDomain: string;
  sessionId: string;
  pageId: string;
  friendlyName?: string;
  environment?: string;
  metadata?: AnalyticsTags;
}) => {
  ensureClarity();
  identifyClarityUser({ shopDomain, sessionId, pageId, friendlyName });

  ensureGoogleAnalytics({
    shopDomain,
    sessionId,
    environment,
    metadata,
  });
};

export const setAnalyticsTags = (tags: AnalyticsTags) => {
  setClarityTags(tags as ClarityTags);

  if (!ensureGoogleAnalytics() || !window.gtag) return;

  try {
    window.gtag('set', toGoogleParams(tags));
  } catch {
    // Ignore analytics failures.
  }
};
